import { Check, Crown } from 'lucide-react';
import { getPlanPrice, PRICING_COMPARISON_ROWS, TIER_LABELS, type SubscriptionTier } from '@/constant/subscription';

const TIERS: SubscriptionTier[] = ['free', 'plus', 'max'];

const PRICE_META: Record<SubscriptionTier, { amount: string; suffix: string }> = {
  free: { amount: '$0', suffix: '/mo' },
  plus: { amount: `$${getPlanPrice('plus', 'monthly').amount.toFixed(2)}`, suffix: '/mo' },
  max: { amount: `$${getPlanPrice('max', 'monthly').amount.toFixed(2)}`, suffix: '/mo' },
};

const ROW_HEIGHT = 'minmax(54px, auto)';
const HEADER_ROW_HEIGHT = '90px';

interface FreeVsProTableProps {
  subjectEntry?: {
    label: string;
    onClick: () => void;
  };
  /** The user's current subscription tier */
  currentTier?: SubscriptionTier;
  /** Whether the current plan is a trial */
  isTrial?: boolean;
  /** Days remaining on trial */
  trialDaysLeft?: number | null;
}

export default function FreeVsProTable({ subjectEntry, currentTier = 'free', isTrial, trialDaysLeft }: FreeVsProTableProps) {
  return (
      <div className="grid grid-cols-[118px_repeat(3,minmax(140px,1fr))] gap-2.5 pt-5">
      <div
        className="grid"
        style={{ gridTemplateRows: `${HEADER_ROW_HEIGHT} repeat(${PRICING_COMPARISON_ROWS.length}, ${ROW_HEIGHT})` }}
      >
        <div className="flex items-end pb-2.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#8B8173]">Feature</span>
        </div>
        {PRICING_COMPARISON_ROWS.map((row) => (
          <div key={row.label} className="flex items-center border-t border-transparent pr-2">
            <div className="flex flex-col items-start gap-1">
              <span className="display-text-semi text-[12px] leading-[1.15] text-[#292524]">{row.label}</span>
              {row.label === 'Subject access' && subjectEntry ? (
                <button
                  type="button"
                  onClick={subjectEntry.onClick}
                  className="display-text inline-flex text-left text-[10px] leading-4 text-[#A56A00] underline decoration-[#D8B25A] underline-offset-2 transition-colors hover:text-[#7C5200]"
                >
                  {subjectEntry.label}
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {TIERS.map((tier) => {
        const isPlus = tier === 'plus';
        const isFree = tier === 'free';
        const isMax = tier === 'max';
        const isCurrent = tier === currentTier;

        return (
          <div key={tier} className="relative">
            {/* "Your plan" tab sticking out above the card */}
            {isCurrent && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 z-10">
                <span className="display-text-semi inline-flex rounded-t-lg bg-[#43CF2C] px-3 py-[3px] text-[9px] uppercase tracking-[0.14em] text-white whitespace-nowrap">
                  {isTrial && trialDaysLeft != null
                    ? `Trial · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`
                    : 'Your plan'}
                </span>
              </div>
            )}
          <div
            className={`relative overflow-hidden rounded-[20px] ${
              isPlus
                ? 'bg-[#FFF4DF]'
                : isMax
                  ? ''
                  : 'bg-[#F3F4F5]'
            }`}
            style={isMax ? { background: 'linear-gradient(180deg, #F3F0FF 0%, #EEE9FF 100%)' } : undefined}
          >
            {/* Recommended ribbon for Plus (when user is on Free) */}
            {isPlus && !isCurrent && currentTier === 'free' && (
              <div
                className="absolute z-10 flex items-center justify-center bg-[#FFB54D] py-[3px]"
                style={{
                  width: '150px',
                  top: '22px',
                  right: '-38px',
                  transform: 'rotate(45deg)',
                  transformOrigin: 'center center',
                }}
              >
                <span className="display-text-semi text-[7px] uppercase tracking-[0.14em] text-[#6F4200]">
                  Recommended
                </span>
              </div>
            )}
            {/* Upgrade ribbon for Max (when user is on Plus) */}
            {isMax && !isCurrent && (currentTier === 'plus') && (
              <div
                className="absolute z-10 flex items-center justify-center bg-[#D9CCFF] py-[3px]"
                style={{
                  width: '150px',
                  top: '22px',
                  right: '-38px',
                  transform: 'rotate(45deg)',
                  transformOrigin: 'center center',
                }}
              >
                <span className="display-text-semi text-[7px] uppercase tracking-[0.14em] text-[#5F45B2]">
                  Upgrade
                </span>
              </div>
            )}

            <div
              className="grid h-full"
              style={{ gridTemplateRows: `${HEADER_ROW_HEIGHT} repeat(${PRICING_COMPARISON_ROWS.length}, ${ROW_HEIGHT})` }}
            >
              <div className="relative flex items-center justify-center px-2.5 text-center">
                <div className="grid justify-items-center gap-1">
                  <div className="flex items-center justify-center gap-1">
                    {!isFree ? <Crown className={`h-3.5 w-3.5 ${isPlus ? 'text-[#C97700]' : isMax ? 'text-[#6B46C1]' : 'text-[#7C8597]'}`} /> : null}
                    <span className={`display-text-semi leading-none text-[13px] ${isPlus ? 'text-[#895100]' : isMax ? 'text-[#5B4A99]' : 'text-[#667085]'}`}>
                      {TIER_LABELS[tier]}
                    </span>
                  </div>
                  <div className="flex items-end justify-center">
                    <span className={`display-text-bold leading-none text-[24px] ${isPlus ? 'text-[#895100]' : isMax ? 'text-[#4C3A94]' : 'text-[#191C1D]'}`}>
                      {PRICE_META[tier].amount}
                    </span>
                    <span className={`mb-[3px] ml-1 text-[10px] leading-none ${isPlus ? 'text-[#D58D2A]' : isMax ? 'text-[#8C7BC7]' : 'text-[#8B95A7]'}`}>
                      {PRICE_META[tier].suffix}
                    </span>
                  </div>
                </div>
              </div>

              {PRICING_COMPARISON_ROWS.map((row) => (
                <div
                  key={`${tier}-${row.label}`}
                  className={`flex items-center justify-center px-2.5 text-center ${
                    isPlus ? 'border-t border-[#F4DEB7]' : 'border-t border-[#E7E8EB]'
                  }`}
                >
                  <TierValue value={row.values[tier]} tier={tier} />
                </div>
              ))}
            </div>
          </div>
          </div>
        );
      })}
    </div>
  );
}

function TierValue({ value, tier }: { value: string; tier: SubscriptionTier }) {
  const isCheck = value === 'Unlimited' || value === 'All subjects' || value === 'All adventures' || value === 'Event pass' || value === '80 sessions/week';
  const lines = formatValueLines(value);

  if (isCheck) {
    return (
      <div className="inline-flex items-center justify-center gap-1.5 text-center text-[15px] font-semibold leading-none text-[#3C8B4A]">
        <Check className="h-3.5 w-3.5 shrink-0" />
        <span className="whitespace-nowrap">
          {lines.map((line) => {
            const suffixMatch = line.match(/^(.+)(\/week)$/);
            if (suffixMatch) {
              return (
                <span key={line} className="inline whitespace-nowrap">
                  {suffixMatch[1]} <span className="text-[10px] opacity-70">{suffixMatch[2]}</span>
                </span>
              );
            }
            return (
              <span key={line} className="inline whitespace-nowrap">
                {line}
              </span>
            );
          })}
        </span>
      </div>
    );
  }

  return (
      <div className={`text-center leading-none ${tier === 'plus' ? 'text-[#C97700]' : tier === 'max' ? 'text-[#6D5DA8]' : 'text-[#667085]'}`}>
      <span className="display-text-semi whitespace-nowrap text-[15px]">
        {lines.map((line) => {
          const suffixMatch = line.match(/^(.+)(\/week)$/);
          if (suffixMatch) {
            return (
              <span key={line} className="inline whitespace-nowrap">
                {suffixMatch[1]} <span className="text-[10px] opacity-70">{suffixMatch[2]}</span>
              </span>
            );
          }
          return (
            <span key={line} className="inline whitespace-nowrap">
              {line}
            </span>
          );
        })}
      </span>
    </div>
  );
}

function formatValueLines(value: string): string[] {
  if (value === 'Expanded access') return ['Expanded access'];
  if (value === 'All subjects') return ['All subjects'];
  if (value === 'All adventures') return ['All adventures'];
  if (value === 'Event pass') return ['Event pass'];
  if (value === '3 sessions/week') return ['3 sessions/week'];
  if (value === '15 sessions/week') return ['15 sessions/week'];
  if (value === '10 hearts') return ['10 hearts'];
  if (value === '20 hearts') return ['20 hearts'];
  if (value === '1 subject') return ['1 subject'];
  if (value === '3 plans') return ['3 plans'];
  return [value];
}
